import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { companyCreate } from "../api/post/companyCreateApi";
import { companyUpdate } from "../api/post/companyEditApi";
import { fetchBankNamesAsync, fetchAccountManagersAsync } from "../redux/features/company/companyThunks";
import { selectBankNames, selectAccountManagers } from "../redux/features/company/companySelectors";


// Kuwait phone number validation regex - exactly 8 digits
const kuwaitPhoneRegex = /^\d{8}$/;

export const NewCompanyForm = ({ onSave, onCancel, editingCompany = null }) => {
  const dispatch = useDispatch();
  
  const banks = useSelector(selectBankNames) || [];
  const managers = useSelector(selectAccountManagers) || [];

  const [form, setForm] = useState({
    companyName: editingCompany?.name || "",
    address1: editingCompany?.address1 || "",
    address2: editingCompany?.address2 || "",
    contactName: editingCompany?.contact_name || "",
    contactPhone: editingCompany?.contact_phone ? (editingCompany.contact_phone.match(/\d{8}$/)?.[0] || "") : "",
    bank: editingCompany?.bank_name || "",
    iban: editingCompany?.iban_number || "",
    swift: editingCompany?.swift_code || "",
    manager: editingCompany?.manager_name || "",
    serviceType: "catering",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    // Fetch bank names and account managers when the component mounts
    dispatch(fetchBankNamesAsync());
    dispatch(fetchAccountManagersAsync());
  }, [dispatch]);

  // Update form when editingCompany changes
  useEffect(() => {
    setForm({
      companyName: editingCompany?.name || "",
      address1: editingCompany?.address1 || "",
      address2: editingCompany?.address2 || "",
      contactName: editingCompany?.contact_name || "",
      contactPhone: editingCompany?.contact_phone ? (editingCompany.contact_phone.match(/\d{8}$/)?.[0] || "") : "",
      bank: editingCompany?.bank_name || "",
      iban: editingCompany?.iban_number || "",
      swift: editingCompany?.swift_code || "",
      manager: editingCompany?.manager_name || "",
      serviceType: "catering",
    });
    
    // Reset validation errors when switching companies
    setError("");
    setPhoneError("");
  }, [editingCompany]);

  const validatePhoneNumber = (phone) => {
    if (!phone) return "Phone number is required";
    if (!kuwaitPhoneRegex.test(phone)) return "Phone number must be exactly 8 digits";
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "contactPhone") {
      const numericValue = value.replace(/\D/g, "").slice(0, 8);
      setForm((prev) => ({ ...prev, [name]: numericValue }));
      setPhoneError(validatePhoneNumber(numericValue));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
    
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const phoneValidationError = validatePhoneNumber(form.contactPhone);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      return;
    }
    
    setLoading(true);
    setError("");

    try {
      const companyData = {
        name: form.companyName,
        address1: form.address1,
        address2: form.address2,
        contact_name: form.contactName,
        contact_phone: form.contactPhone,
        bank_name: form.bank,
        iban_number: form.iban,
        swift_code: form.swift,
        manager_name: form.manager,
        service_type: "catering"
      };

      let response;
      if (editingCompany) {
        // Update existing company
        response = await companyUpdate(editingCompany.id, companyData);
      } else {
        // Create new company
        response = await companyCreate(companyData);
      }
      
      if (onSave) onSave({ formData: form, apiResponse: response, isEdit: !!editingCompany });
      
    } catch (err) {
      const action = editingCompany ? "update" : "create";
      setError(err.response?.data?.message || `Failed to ${action} company. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-4 border border-gray-200 rounded-xl bg-white shadow-md">
      <h2 className="text-xl font-bold text-gray-700 mb-2">
        {editingCompany ? "Edit Company" : "Create New Company"}
      </h2>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded-lg text-sm">
          {error}
        </div>
      )}
      
      <input
        name="companyName"
        value={form.companyName}
        onChange={handleChange}
        placeholder="Company Name"
        required
        disabled={loading}
        className="border border-gray-300 bg-neutral-50 rounded-xl px-4 py-2 h-12 focus:ring-1 focus:ring-gray-300 focus:outline-none placeholder-gray-400 text-gray-500 font-bold disabled:opacity-50"
      />
      <input
        name="address1"
        value={form.address1}
        onChange={handleChange}
        placeholder="Company Address 1"
        required
        disabled={loading}
        className="border border-gray-300 bg-neutral-50 rounded-xl px-4 py-2 h-12 focus:ring-1 focus:ring-gray-300 focus:outline-none placeholder-gray-400 text-gray-500 font-bold disabled:opacity-50"
      />
      <input
        name="address2"
        value={form.address2}
        onChange={handleChange}
        placeholder="Company Address 2"
        disabled={loading}
        className="border border-gray-300 bg-neutral-50 rounded-xl px-4 py-2 h-12 focus:ring-1 focus:ring-gray-300 focus:outline-none placeholder-gray-400 text-gray-500 font-bold disabled:opacity-50"
      />
      <input
        name="contactName"
        value={form.contactName}
        onChange={handleChange}
        placeholder="Contact Person Name"
        required
        disabled={loading}
        className="border border-gray-300 bg-neutral-50 rounded-xl px-4 py-2 h-12 focus:ring-1 focus:ring-gray-300 focus:outline-none placeholder-gray-400 text-gray-500 font-bold disabled:opacity-50"
      />
      
      <div>
        <div className="relative">
          <input
            name="contactPhone"
            value={form.contactPhone}
            onChange={handleChange}
            placeholder="Contact Person Phone"
            required
            disabled={loading}
            className={`border rounded-xl pl-14 pr-4 py-2 h-12 w-full focus:ring-1 focus:outline-none placeholder-gray-400 text-gray-500 font-bold disabled:opacity-50 ${
              phoneError ? "border-red-400 bg-red-50" : "border-gray-300 bg-neutral-50 focus:ring-gray-300"
            }`}
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 font-bold pointer-events-none">
            +965
          </div>
        </div>
        {phoneError && (
          <div className="text-red-500 text-xs mt-1 ml-1">
            {phoneError}
          </div>
        )}
      </div>
      
      <select
        name="bank"
        value={form.bank}
        onChange={handleChange}
        required
        disabled={loading || banks.length === 0}
        className="border border-gray-300 bg-neutral-50 rounded-xl px-4 py-2 h-12 focus:ring-1 focus:ring-gray-300 focus:outline-none text-gray-500 font-bold disabled:opacity-50"
      >
        <option value="">Bank Name</option>
        {banks.map((b) => (
          <option key={b.id} value={b.name}>{b.name}</option>
        ))}
      </select>
      <input
        name="iban"
        value={form.iban}
        onChange={handleChange}
        placeholder="Account Number (IBAN)"
        required
        disabled={loading}
        className="border border-gray-300 bg-neutral-50 rounded-xl px-4 py-2 h-12 focus:ring-1 focus:ring-gray-300 focus:outline-none placeholder-gray-400 text-gray-500 font-bold disabled:opacity-50"
      />
      <input
        name="swift"
        value={form.swift}
        onChange={handleChange}
        placeholder="Swift Code"
        required
        disabled={loading}
        className="border border-gray-300 bg-neutral-50 rounded-xl px-4 py-2 h-12 focus:ring-1 focus:ring-gray-300 focus:outline-none placeholder-gray-400 text-gray-500 font-bold disabled:opacity-50"
      />
      <select
        name="manager"
        value={form.manager}
        onChange={handleChange}
        required
        disabled={loading || managers.length === 0}
        className="border border-gray-300 bg-neutral-50 rounded-xl px-4 py-2 h-12 focus:ring-1 focus:ring-gray-300 focus:outline-none text-gray-500 font-bold disabled:opacity-50"
      >
        <option value="">Account Manager</option>
        {managers.map((m) => (
          <option key={m.id} value={m.name}>{m.name}</option>
        ))}
      </select>
      
      <div className="flex items-center gap-4 mt-2">
        <button
          type="submit"
          disabled={loading || phoneError || !form.companyName || !form.bank || !form.manager}
          className="flex-1 bg-[#7B4F27] text-white font-bold rounded-xl h-12 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#6a421f]"
        >
          {loading ? "Saving..." : editingCompany ? "Update Company" : "Save New Company"}
        </button>
        {onCancel && (
          <button
            type="button"
            className="flex-1 bg-gray-200 text-gray-700 font-bold rounded-xl h-12 disabled:opacity-50 hover:bg-gray-300"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

export default NewCompanyForm; 